import React from 'react';
import {
  Flex,
  Box,
  Table,
  Tbody,
  Text,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  IconButton,
} from '@chakra-ui/react';
import {
  createColumnHelper,
} from '@tanstack/react-table';
import PageTable from 'components/layout/PageTable';
import AdminTableCell from 'components/layout/AdminTableCell';
import SortableTableHeader from 'components/layout/SortableTableHeader';
import { useSortableAdminTable } from 'hooks/useSortableAdminTable';
import { getSortableColumnOptions } from 'utils/tableColumnOptions';
import TablePagination from 'components/layout/TablePagination';
import ProductsEditModal from './ProductsEditModal'; // Importa el nuevo modal
import DeleteModal from '../../components/DeleteModal/DeleteModal';
import { formatNumber } from '../../utils/transform';
import { formatMoneyDual } from '../../utils/currencyDisplay';
import useBcuUsdRate from '../../hooks/useBcuUsdRate';
import ModelListCell, {
  getProductModelLabels,
} from 'components/common/ModelListCell';

const columnHelper = createColumnHelper();

export default function ProductsTable({ columnsData, data, page, setPage, totalPages, pageSize, setPageSize, handleUpdateProduct, sorting, onSortingChange }) {
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [modalState, setModalState] = React.useState({ isOpen: false });
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const [productToDelete, setProductToDelete] = React.useState(null);
  const [deleteModalState, setDeleteModalState] = React.useState({ isOpen: false });
  const toast = useToast();
  const { rate: usdRate } = useBcuUsdRate();
  const columns = React.useMemo(
    () => createColumns(columnsData, usdRate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsData, usdRate],
  );

  const table = useSortableAdminTable({
    data,
    columns,
    sorting,
    onSortingChange,
  });

  const openEditModal = (rowData) => {
    setSelectedProduct(rowData);
    setModalState({ isOpen: true });
  };

  const closeEditModal = () => {
    setModalState({ isOpen: false });
    setSelectedProduct(null);
  };

  const openDeleteModal = (rowData) => {
    setProductToDelete(rowData);
    setDeleteModalState({ isOpen: true });
  };

  const closeDeleteModal = () => {
    setDeleteModalState({ isOpen: false });
    setProductToDelete(null);
  };

  const handleDelete = async () => {
    const updatedProduct = { ...productToDelete, enabled: !productToDelete.enabled };
    try {
      await handleUpdateProduct(updatedProduct);
      closeDeleteModal();
    } catch (error) {
      toast({
        title: 'Error al actualizar el producto',
        description: error.message || 'No se pudo actualizar el producto.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  function createColumns(columnsData, usdRate) {
    return columnsData.map((col) =>
      columnHelper.accessor(col.accessor, {
        ...getSortableColumnOptions(col),
        header: () => (
          <Text fontSize={{ sm: '10px', lg: '12px' }}>{col.Header}</Text>
        ),
        cell: (info) => {
          const value = info.getValue();
          let displayValue = value;

          if (col.accessor === 'model.description') {
            const row = info.row.original;
            return (
              <Flex align="center" minW={0} maxW="100%">
                <ModelListCell labels={getProductModelLabels(row)} />
                {col.actions &&
                  col.actions.map((action, index) => (
                    <IconButton
                      key={index}
                      aria-label={action.label}
                      icon={action.icon}
                      size="sm"
                      variant="ghost"
                      colorScheme={action.colorScheme}
                      ml={2}
                      onClick={() => {
                        if (action.type === 'edit') {
                          openEditModal(info.row.original);
                        } else if (action.type === 'delete') {
                          openDeleteModal(info.row.original);
                        }
                      }}
                    />
                  ))}
              </Flex>
            );
          }

          if (col.accessor === 'currentCost') {
            const row = info.row.original;
            displayValue = formatMoneyDual(
              value,
              row.currency?.code || 'UYU',
              usdRate,
            );
          } else if (col.accessor === 'currentStock' || col.accessor === 'minStock') {
            const isService =
              String(info.row.original?.category?.name || '')
                .trim()
                .toLowerCase()
                .startsWith('servicio');
            displayValue = isService ? 'No aplica' : formatNumber(value);
          }

          if (col.accessor === 'ivaIncluded') {
            displayValue = value ? 'Sí' : 'No';
          }
          
          return (
            <Flex align="center">
              <Text fontSize="sm" fontWeight="normal">
                {displayValue}
              </Text>
              {col.actions &&
                col.actions.map((action, index) => (
                  <IconButton
                    key={index}
                    aria-label={action.label}
                    icon={action.icon}
                    size="sm"
                    variant="ghost"
                    colorScheme={action.colorScheme}
                    ml={2}
                    onClick={() => {
                      if (action.type === 'edit') {
                        openEditModal(info.row.original);
                      }
                      else if(action.type === 'delete') {
                        openDeleteModal(info.row.original);
                      }
                    }}
                  />
                ))}
            </Flex>
          );
        },
      }),
    );
  }

  return (
    <PageTable flexDirection="column" w="100%" px="0px" >
      <Box>
        <PageTable.Scroll>
          <Table variant="simple" color="gray.700" mb="24px" mt="12px">
                    <Thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <SortableTableHeader
                              key={header.id}
                              header={header}
                              borderColor={borderColor}
                            />
                          ))}
                        </Tr>
                      ))}
                    </Thead>
                    <Tbody>
                      {table.getRowModel().rows.map((row) => (
                        <Tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                                <AdminTableCell key={cell.id} cell={cell} />
                              ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
        </PageTable.Scroll>
          <TablePagination
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
      </Box>

      <ProductsEditModal
        isOpen={modalState.isOpen}
        onClose={closeEditModal}
        product={selectedProduct}
        onUpdate={handleUpdateProduct}
      />
      <DeleteModal
        isOpen={deleteModalState.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        name={`${productToDelete?.code} - ${productToDelete?.name}`}
      />
    </PageTable>
  );
}